import type { AbstractIntlMessages } from 'next-intl'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, type SupportedLocale } from '@/i18n/locales'
import { getSectionMessageNamespaces, pickMessageNamespaces } from './client-message-namespaces'

/** The full catalog for one locale, from whichever side of the render is asking. */
export function loadLocaleCatalog(locale: SupportedLocale): Promise<Record<string, unknown>> {
  // Server renders read the catalog file every function already ships. Keep the
  // browser imports out of the server branch: compiling them there adds every
  // locale to each server function, and function size decides which routes
  // Vercel bundles together, so a lone low-traffic route starts cold far more often.
  if (typeof window === 'undefined') {
    return import('./server-messages').then(({ loadServerMessages }) => loadServerMessages(locale))
  } else {
    const loaders: Record<SupportedLocale, () => Promise<{ default: Record<string, unknown> }>> = {
      en: () => import('../../../messages/en.json'),
      zh: () => import('../../../messages/zh.json'),
      hi: () => import('../../../messages/hi.json'),
      es: () => import('../../../messages/es.json'),
      fr: () => import('../../../messages/fr.json'),
      ar: () => import('../../../messages/ar.json'),
      bn: () => import('../../../messages/bn.json'),
      pt: () => import('../../../messages/pt.json'),
      ru: () => import('../../../messages/ru.json'),
      ja: () => import('../../../messages/ja.json'),
      it: () => import('../../../messages/it.json'),
      he: () => import('../../../messages/he.json'),
      de: () => import('../../../messages/de.json'),
      el: () => import('../../../messages/el.json'),
      pl: () => import('../../../messages/pl.json'),
    }
    return loaders[locale]().then(({ default: messages }) => messages)
  }
}

const namespaces = getSectionMessageNamespaces('workspace', {
  includeGlobal: true,
})
const catalogs = new Map<string, Promise<AbstractIntlMessages>>()

// Only public translation text is cached here. Loading the versioned locale
// chunk once avoids serializing this same catalog into every authenticated
// HTML/RSC response. Keep the promise stable for React.use across Suspense retries.
export function getWorkspaceMessages(
  locale: string,
  additionalNamespaces: readonly string[] = []
): Promise<AbstractIntlMessages> {
  const supportedLocale = SUPPORTED_LOCALES.includes(locale as SupportedLocale)
    ? (locale as SupportedLocale)
    : DEFAULT_LOCALE
  const key = JSON.stringify([supportedLocale, ...additionalNamespaces])
  let catalog = catalogs.get(key)
  if (!catalog) {
    catalog = loadLocaleCatalog(supportedLocale).then((messages) =>
      pickMessageNamespaces(messages, [...namespaces, ...additionalNamespaces])
    )
    catalogs.set(key, catalog)
  }
  return catalog
}
